import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, take } from 'rxjs/operators';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from 'app/config.service';
import { ContainerService } from 'app/private/_core/_services/container.service';
import { Constants } from 'app/constants';
import * as Sentry from '@sentry/browser';
import { environment } from 'environments/environment';
import * as GripCDP from 'grip-cdp-tracker';
import { GripCDPConfiguration, FromTypeEnum } from 'grip-cdp-tracker/lib/esm/cdp.model';
import { HostedBuyerConfigModel } from './../_models/hosted-buyer-config.model';
import { Router } from '@angular/router';
import { RegistrationProviderTypeEnum } from '../_enums/registration-provider-type.enum';
import { AuthService } from './auth.service';
import { UserPermissionsType } from 'app/private/profile/export/export.model'
import { ApiService } from './api.service';
import { FeatureControls, FeatureControlsModel } from '../_models/feature-controls.model';

@Injectable({
	providedIn: 'root'
})
export class UserService {
	API_URL: string;

	// Config
	public authToken = '';
	public teamsAuthToken = '';
	public headers: any = {};
	public teamsHeaders: any = {
		'Content-type': 'application/json',
		'login-source': 'web'
	};
	public user: any = null;
	public loggedIn = false;
	public hasLoggedInThisSession = false;
	public hasGetMeData = false;
	public isFirstLogin = false;
	public logoutApisCalled = false;

	public chatLoaded = false;
	public companyChatLoaded = false;

	public canSetPassword = false;
	public hasPassword = false;
	public hasMeetingOrganizerLimit = false;

	public userGroups: any[] = [];

	// TODO move all of this to HB service
	public hbConfigModePhaseSet = false;
	public hbConfig: HostedBuyerConfigModel | null = null;
	public hbConfigSubject = new Subject<HostedBuyerConfigModel>();

	public hbMustMeetTooltip = '';
	public hbMeetTooltip = '';
	public hbNoThanksTooltip = '';

	public interestedInYouCount = 0;

	public userInVirtualMeeting = false;

	public tooltipsChangedSubject = new Subject<void>();
	public userEmailChangedSubject = new Subject<string>();

	public userPermissionsSubject: BehaviorSubject<Record<UserPermissionsType,boolean>> = new BehaviorSubject<Record<UserPermissionsType,boolean>>(null);
	public featureControlsSubject: BehaviorSubject<FeatureControls | null> = new BehaviorSubject<FeatureControls | null>(null);

	public triggerSignOutSubject = new Subject<void | boolean>();

	// CDP
	public gripCdpCookieAcceptedSubject = new BehaviorSubject<boolean>(false);
	private gripCDPConfig: GripCDPConfiguration;

	// Feature Controls
	public featureControls: FeatureControls = null;

	constructor(
		public configService: ConfigService,
		private http: HttpClient,
		private apiService: ApiService,
		private router: Router,
		private containerService: ContainerService,
		private authService: AuthService,
	) {
		// Init User
		this.loggedIn =
			localStorage &&
			typeof localStorage.getItem === 'function' &&
			!!localStorage.getItem('user');
		if (this.loggedIn) {
			this.user = JSON.parse(localStorage.getItem('user'));
		}

		this.API_URL = environment.apiUrl;

		// Init CDP Config
		this.configService.gripCdpTokenLoaded.subscribe((isTokenLoaded: boolean) => {
			if (isTokenLoaded) {
				this.gripCDPConfig = {
					app_id: this.configService.applicationId || 0,
					container_id: this.configService.containerId || 0,
					key: this.configService.gripCdpToken,
					from_type: FromTypeEnum.THING,
					version: `web_${Constants.CONSTANT_WEB_VERSION.slice(4)}`
				};
				if (environment.name !== 'prod') {
					// override default (prod) with env-appropriate
					// NB: will also enable debug
					this.gripCDPConfig.domain = environment.analyticsUrl;
				}
				this.gripCdpCookieAcceptedSubject.subscribe((isCookieAccepted: boolean) => {
					if (isCookieAccepted) {
						this.initGripCdp();
					}
				});
			}
		})
	}

	public getUserFeatureControls(params: {team_id?: number, event_id?: number} = {}): void {
		if (!this.user?.id) {
			return;
		}

		let query = `${this.API_URL}container/${this.configService.containerId}/thing/${this.user.id}/feature-control`;
		if (params?.team_id && params?.event_id) {
			query += `?team_id=${params.team_id}&event_id=${params.event_id}`;
		} else if (params?.event_id) {
			query += `?event_id=${params.event_id}`;
		}

		this.http
		.get(query)
		.pipe(map((result: any) => result?.data))
		.subscribe(
			(res: any) => {
				this.featureControls = res || {
					badge_scanning: 0,
					digital_badge: 0,
					lead_qualification: 0,
					session_check_in: 0
				};
				this.featureControlsSubject.next(this.featureControls);
			},
			error => {
				console.error("Error fetching user feature controls:", error);
				this.featureControls = {
					badge_scanning: 0,
					digital_badge: 0,
					lead_qualification: 0,
					session_check_in: 0
				};
				this.featureControlsSubject.next(this.featureControls);
			}
		);
	}

	public getUserPermissions() {
		this.http
			.get(this.API_URL + 'user/permission')
			.pipe(map((result: any) => result.data))
			.subscribe(
				permissions => {
					this.setUserPermissions(permissions);
				},
				error => {
					console.error(error);
				}
			)
	}

	private setUserPermissions(permissions): void {
		const containerPermissions = permissions?.container_permissions?.find(
			event => event.container_id === this.configService.containerId
		);
		const containerConversionPermissions = permissions?.container_conversion_permissions?.find(
			event => event.container_id === this.configService.containerId
		);

		const canExportMeetings: boolean =
			this.checkPermissionAction(containerPermissions, 'can_export_meeting')
			&& this.checkPermissionAction(containerPermissions, 'can_meet');
		const canExportConnections: boolean =
			this.checkPermissionAction(containerPermissions, 'can_export_connections')
			|| this.checkPermissionAction(containerConversionPermissions, 'can_export_connections');
		const canChat: boolean = this.checkPermissionAction(containerPermissions, 'can_chat');
		const canViewAvailability: boolean = this.checkPermissionAction(containerPermissions, 'can_view_availability');
		const canEditAvailability: boolean =
			this.checkPermissionAction(containerPermissions, 'can_edit_availability')
			|| this.checkPermissionAction(containerConversionPermissions, 'can_edit_availability');

		const canUseAIAssistant: boolean = this.checkPermissionAction(containerPermissions, 'can_interact_ai_assistant');

		const userPermissions: Record<UserPermissionsType,boolean> =
			{
				canExportMeetings,
				canExportConnections,
				canChat,
				canViewAvailability,
				canEditAvailability,
				canUseAIAssistant
			}

		this.userPermissionsSubject.next(userPermissions);
	}

	public checkPermissionAction(permissions: any, action: string): boolean {
		if (!permissions) return false;
		return permissions?.permissions.some(
			permission => permission.action === action
		);
	}

	public setDefaultHeaders(): void {
		this.authToken = '';
		this.headers = {
			Authorization: this.configService.authorization_token || '',
			'login-source': 'web',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'Accept-Language': this.configService.getUserLang(),
			'X-Grip-Version': Constants.CONSTANT_WEB_VERSION,
			Version: Constants.CONSTANT_WEB_VERSION.substring(4)
		};
		if (environment.betaContainers.includes(this.configService.containerId)) {
			this.headers['X-Grip-Beta'] = 'on';
		}
	}

	public setAuthHeaders(token: string): void {
		this.authToken = token;
		this.headers = {
			'X-Authorization': `${token}`,
			'login-source': 'web',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'Accept-Language': this.configService.getUserLang(),
			'X-Grip-Version': Constants.CONSTANT_WEB_VERSION,
			Version: Constants.CONSTANT_WEB_VERSION.substring(4)
		};
		if (environment.betaContainers.includes(this.configService.containerId)) {
			this.headers['X-Grip-Beta'] = 'on';
		}
	}

	public setTeamsAuthHeaders(token: string): void {
		this.teamsAuthToken = token;
		this.teamsHeaders = {
			Authorization: `Bearer ${token}`,
			'login-source': 'web',
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'Accept-Language': this.configService.getUserLang(),
			'X-Grip-Version': Constants.CONSTANT_WEB_VERSION,
			Version: Constants.CONSTANT_WEB_VERSION.substring(4)
		};
		if (environment.betaContainers.includes(this.configService.containerId)) {
			this.teamsHeaders['X-Grip-Beta'] = 'on';
		}
	}

	public removeTeamsHeaders(): void {
		this.teamsAuthToken = '';
		this.teamsHeaders = {
			'Content-Type': 'application/json',
			'login-source': 'web'
		};
		if (environment.betaContainers.includes(this.configService.containerId)) {
			this.teamsHeaders['X-Grip-Beta'] = 'on';
		}
	}

	public setLogin(userData: any, fromLogin: boolean, isGetMeData: boolean) {
		if (this.authToken) {
			userData.access_token = this.authToken;
		}

		this.hasMeetingOrganizerLimit = typeof userData?.permissions?.meeting_organizer_limit === 'number';

		this.setLocalData(userData);

		this.loggedIn = true;
		if (fromLogin) {
			this.hasLoggedInThisSession = true;
			if (isGetMeData) {
				userData.container_type_id = userData.type_id;
				this.configService.onboardingComplete = !!userData.onboarding_seen;
				this.hasGetMeData = true;
			}
		}
		this.user = userData;
		this.setAuthHeaders(this.user.access_token);

		// send user data to sentry
		Sentry.configureScope(scope => {
			scope.setUser({
				id: userData.id ? userData.id : '',
			});
		});
	}

	public setLocalData(userData): void {
		if (!userData) return;
		userData.containerId = this.configService.containerId;
		this.containerService.applicationUserContainers.pipe(take(1)).subscribe(containers => {
			userData.additionalContainerIds = containers.map(
				container => container.id
			);
			if (localStorage && typeof localStorage.setItem === 'function') {
				localStorage.setItem('user', JSON.stringify(userData));
			}
		});
	}

	public getToken(): string {
		return this.user.access_token;
	}

	public setUserValue(userKey: string, userValue: any) {
		this.user[userKey] = userValue;
	}

	public logout() {
		// logout API
		if (!this.logoutApisCalled) {
			this.logoutApi$.subscribe({
				error: error => console.error('user.service::logout::logoutApi$ ', error)
			});
		}

		// stop GripCDP
		GripCDP.stopHeartbeat(); // jic
		GripCDP.init({from_id: -1});

		// clear user data and set default headers
		this.logoutClearData();
		this.setDefaultHeaders();
		this.removeTeamsHeaders();

		// check local storage for oidc logout id_token
		let oidcLogoutRedirectUrl: null | string = null;
		if (localStorage && typeof localStorage.getItem === 'function') {
			const openid_id_token = localStorage.getItem('grip_oidc_id_token');
			if (this.configService.sso_logout_url && openid_id_token) {
				const queryParams: string = new URLSearchParams({
					client_id: 'grip',
					post_logout_redirect_uri: `${window.location.origin}/${this.configService.configName}/login`,
					id_token_hint: openid_id_token,
				}).toString();
				oidcLogoutRedirectUrl = `${this.configService.sso_logout_url}?${queryParams}`;
			}
		}

		// clear local storage
		if (localStorage && typeof localStorage.removeItem === 'function') {
			localStorage.removeItem('user');
			localStorage.removeItem('userRegister');
			localStorage.removeItem('onboardingDone');
			localStorage.removeItem('teams_token');
			localStorage.removeItem('grip_oidc_id_token');
			localStorage.removeItem('selectedTeamId');
		}

		// mute OneSignal notifications and delete user tag
		if (this.configService.oneSignalAppId && (window as any).OneSignal) {
			const OneSignal = (window as any).OneSignal || [];
			OneSignal.push(() => {
				OneSignal.deleteTag('thingID');
				// Remove the external_user_id associated with OneSignal Player
				OneSignal.removeExternalUserId();
			});
			OneSignal.push(['setSubscription', false]);
		}

		// finally if oidcLogoutRedirectUrl is set, redirect to it
		if (oidcLogoutRedirectUrl) {
			window.location.href = oidcLogoutRedirectUrl;
		}
	}

	public get logoutApi$(): Observable<any> {
		if (
			this.configService.sso === 1 &&
			(
				!this.isGuestType ||
				(
					this.user.is_guest &&
					!this.configService.isGuestBypassSSOLogin
				)
			)
		) {
			// sso enabled AND
			// regular user OR guest user with bypass disabled
			return this.authService.userSSOLogout();
		} else {
			return this.authService.userLogout();
		}
	}

	private logoutClearData() {
		this.user = null;
		this.hasLoggedInThisSession = false;
		this.hasGetMeData = false;
		this.loggedIn = false;
		this.chatLoaded = false;
		this.companyChatLoaded = false;
		this.canSetPassword = false;
		this.hasPassword = false;
		this.userGroups = [];
		this.hbConfigModePhaseSet = false;
		this.hbConfig = null;
		this.hbMustMeetTooltip = '';
		this.hbMeetTooltip = '';
		this.hbNoThanksTooltip = '';
		this.interestedInYouCount = 0;
		this.hasMeetingOrganizerLimit = false;
		this.userPermissionsSubject.next(null);
	}

	public isLoggedIn() {
		if (
			!this.user &&
			localStorage &&
			typeof localStorage.getItem === 'function' &&
			localStorage.getItem('user') != undefined
		) {
			this.user = JSON.parse(localStorage.getItem('user'));
			// if user is not from the same application, logout
			if (this.user.application_id !== this.configService.applicationId) {
				this.loggedIn = false;
				this.user = null;
				localStorage.removeItem('user')
			} else {
				this.loggedIn = true;
				this.setAuthHeaders(this.user.access_token);
			}
		}
		return this.loggedIn;
	}

	public async verifyOtpToken(otpToken: string): Promise<boolean> {
		const response = await this.authService.checkOtp(otpToken).toPromise();
		if (response) {
			this.setAuthHeaders(response.user_token);
			this.isFirstLogin = response.first_login;

			const userData = await this.authService.getMe().toPromise();
			userData.chat_token = response.chat_token;
			this.setLogin(userData, true, true);
			this.loggedIn = true;

			return true;
		} else {
			return false;
		}
	}

	public getLoginPath(): string {
		const loginUrlSuffix: string =
			this.configService.sso === 0
			&& this.configService.loginVersion === 2
				? '/event-login'
				: '/login';

		return `${ this.configService.configName }${ loginUrlSuffix }`;
	}

	public goToLogin(): void {
		const loginUrl: string = this.getLoginPath();
		this.router.navigate( [ loginUrl ] );
	}

	public goToSignUp(): void {
		const {
			registration_type,
			external_registration_url
		} = this.configService.registration;

		if ( registration_type === RegistrationProviderTypeEnum.external ) {
			this.goToExternalSignUp( external_registration_url );
		}
		else if ( registration_type === RegistrationProviderTypeEnum.open ) {
			this.goToGripSignUp();
		}
		else {
			this.goToHome();
		}
	}

	public goToGripSignUp(): void {
		const signUpUrl: string = `${ this.configService.configName }/signup`;
		this.router.navigate( [ signUpUrl ] );
	}

	public goToExternalSignUp( external_registration_url ): void {
		if ( external_registration_url ) {
			window.location.href = external_registration_url;
		}
	}

	public goToHome(): void {
		const signUpUrl: string = `${ this.configService.configName }/app/home`;
		this.router.navigate( [ signUpUrl ] );
	}

	private initGripCdp() {
		// use -1 as id when from userless source such as email action
		GripCDP.init({
			from_id: this.user?.id > 0 ? this.user.id : -1,
			...this.gripCDPConfig
		});
	}

	public initGripCdpIfConsented(): void {
		if (this.gripCdpCookieAcceptedSubject.value && this.gripCDPConfig) {
			this.initGripCdp();
		}
	}

	public get isGuestType() {
		return this.user && (this.user.is_guest || this.user.is_anonymous);
	}
}
